"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Player = void 0;
exports.requestPlayer = requestPlayer;
const tslib_1 = require("tslib");
const events_typed_1 = require("@video/events-typed");
const state_flow_1 = require("@video/state-flow");
const api_1 = require("../../api");
const player_options_1 = require("../../api/player-options");
const errors_1 = require("../../internal/errors");
const bind_1 = require("../../internal/utils/bind");
const context_1 = require("../../internal/utils/context/context");
const logger_1 = require("../../internal/utils/logger");
const register_1 = require("./drivers/register");
const host_supervisor_1 = require("./host-supervisor");
const media_loader_1 = require("./media-loader");
const state_flow_2 = require("./state-flow");
const Audio = tslib_1.__importStar(require("./state-flow/controls/audio"));
const Playback = tslib_1.__importStar(require("./state-flow/controls/playback"));
const Quality = tslib_1.__importStar(require("./state-flow/controls/quality"));
const Video = tslib_1.__importStar(require("./state-flow/controls/video"));
const helpers_1 = require("./state-flow/helpers");
const Host = tslib_1.__importStar(require("./state-flow/manifest/host"));
const Manifest = tslib_1.__importStar(require("./state-flow/manifest/manifest"));
function requestPlayer(ctx, url, rawOptions) {
    const env_1 = { stack: [], error: void 0, hasError: false };
    try {
        ctx = (0, logger_1.extendContext)(ctx, Player);
        const stack = tslib_1.__addDisposableResource(env_1, new DisposableStack(), false);
        const options = player_options_1.PlayerOptions.parse(rawOptions);
        const drivers = register_1.registeredDrivers.map(Driver => new Driver((0, logger_1.extendContext)(ctx, Driver)));
        const player = stack.use(new Player(ctx));
        const mediaLoader = stack.use(new media_loader_1.MediaLoader((0, logger_1.extendContext)(ctx, media_loader_1.MediaLoader)));
        const supervisor = stack.use(new host_supervisor_1.HostSupervisor((0, logger_1.extendContext)(ctx, host_supervisor_1.HostSupervisor)));
        const stateFlow = stack.use(state_flow_2.PlayerStateFlow.initialize(player, mediaLoader, supervisor, ...drivers));
        stateFlow.dispatch("optionsApply", { options }).expect(state_flow_1.ResultKind.OK);
        stateFlow.dispatch("manifestReplaceUrl", { url }).expect(state_flow_1.ResultKind.OK);
        // move disposer to context instead of this function
        // so everything will be disposed when context is canceled
        const disposer = stack.move();
        (0, context_1.onceCanceled)(ctx).finally(() => disposer.dispose());
        return player;
    }
    catch (e_1) {
        env_1.error = e_1;
        env_1.hasError = true;
    }
    finally {
        tslib_1.__disposeResources(env_1);
    }
}
class Player extends events_typed_1.EventEmitter {
    constructor(ctx) {
        super();
        this.ctx = ctx;
        // private mediaLoader: MediaLoader;
        this.isDisposed = false;
        (0, bind_1.makeBounded)(this);
        (0, context_1.onceCanceled)(ctx).finally(this[Symbol.dispose]);
    }
    //#region Fields
    get logger() {
        return this.ctx.logger;
    }
    set logger(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    //#endregion
    get supportsLowLatency() {
        return false;
    }
    set supportsLowLatency(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get lowLatency() {
        return false;
    }
    set lowLatency(value) {
    }
    // private mediaElementSupervisor: MediaElementSupervisor;
    get currentQuality() {
        return null;
    }
    /**
     * @deprecated It does nothing now and will be removed in the future
     */
    get currentPlayer() {
        return this;
    }
    /**
     * @deprecated It does nothing now and will be removed in the future
     */
    set currentPlayer(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    /**
     * @deprecated It does nothing now and will be removed in the future
     */
    get availablePlayers() {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    /**
     * @deprecated It does nothing now and will be removed in the future
     */
    set availablePlayers(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get attached() {
        return !Host.Detached.is(this.sf.host);
    }
    get autoPlay() {
        return this.sf.playback.autoPlay;
    }
    set autoPlay(value) {
        this.sf.dispatch(value ? "requestEnableAutoPlay" : "requestDisableAutoPlay", {}).expect(state_flow_1.ResultKind.OK);
    }
    get blurred() {
        return Quality.Blurred.is(this.sf.quality);
    }
    set blurred(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get driverFailover() {
        const [_, props] = (0, helpers_1.getCurrentDriver)(this.sf);
        return props.timeout === 0;
    }
    /**
     * @deprecated Use `timeout` option instead
     */
    set driverFailover(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get forcedMute() {
        return Audio.ForcedMute.is(this.sf.audio);
    }
    /**
     * @deprecated Forced mute is handled automatically, don't try to change that.
     * Use `localAudioMuted` if you need to mute the player.
     */
    set forceMute(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get format() {
        const [_, props] = (0, helpers_1.getCurrentDriver)(this.sf);
        return props.driverFormat;
    }
    get localAudioMuted() {
        return Audio.Muted.is(this.sf.audio);
    }
    set localAudioMuted(value) {
        this.sf.dispatch(value ? "requestMute" : "requestUnMute", {}).expect(state_flow_1.ResultKind.OK);
    }
    get localAudioVolume() {
        return this.sf.audio.volume;
    }
    set localAudioVolume(value) {
        this.sf.dispatch("requestVolumeChange", { volume: value }).expect(state_flow_1.ResultKind.OK);
    }
    get localVideoPaused() {
        return Video.Paused.is(this.sf.video);
    }
    set localVideoPaused(value) {
        this.sf.dispatchAsync(value ? "requestPause" : "requestPlay", {});
    }
    get lowPowerMode() {
        // @todo do we really need to expose that?
        return false;
    }
    /**
     * @deprecated Low power mode is handled automatically, don't try to change that.
     */
    set lowPowerMode(value) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    get poster() {
        var _a, _b;
        return (_b = (_a = this.sf.manifest.json) === null || _a === void 0 ? void 0 : _a.previewImg) !== null && _b !== void 0 ? _b : null;
    }
    set poster(value) {
        this.sf.dispatch("requestOverridePoster", { url: value }).
            expect(state_flow_1.ResultKind.OK);
    }
    set preferredLevel(level) {
        this.sf.dispatch("requestForcedQuality", { quality: level }).
            expect(state_flow_1.ResultKind.OK);
    }
    initStateFlow(sf) {
        this.sf = sf;
        sf.onCommit(Host.Attached, this.onHostAttached);
        sf.onCommit(Manifest.Online, this.onManifestOnline);
    }
    onHostAttached(states) {
    }
    async onManifestOnline(states) {
        const [drivers, current] = (0, helpers_1.getSupportedDrivers)(states);
        if (current === -1 && drivers.length > 0) {
            let result = state_flow_1.Result.ignore();
            let next = current >= drivers.length - 1 ? 0 : current + 1;
            while (result.kind !== state_flow_1.ResultKind.OK) {
                result = await this.sf.dispatchAsync("driverSwitch", { driver: drivers[next] });
                next = next >= drivers.length - 1 ? 0 : next + 1;
            }
            if (this.sf.playback.autoPlay) { // autoplay
                (await this.sf.dispatchAsync("internalPlay", {})).expect(state_flow_1.ResultKind.OK);
            }
        }
    }
    attachTo(el) {
        this.sf.dispatch("hostElementAttach", { host: el }).expect(state_flow_1.ResultKind.OK);
    }
    isImplements(feature) {
        return true;
    }
    /**
     * @deprecated The method is not exposed anymore and will be removed completely in the future
     */
    isSupported() {
        return Promise.resolve(true);
    }
    updatePosterWithLatestFrame() {
        this.sf.dispatch("requestUpdatePosterFrame", {}).
            expect(state_flow_1.ResultKind.OK);
    }
    toJSON() {
        return {};
    }
    getClosestQuality(score) {
        return null;
        //return findClosestQuality(score, ...);
    }
    async nextDriver() {
        if ((0, state_flow_1.isTransitioning)(this.sf)) {
            throw new Error("cannot switch player while state is in transition");
        }
        const [drivers, current] = (0, helpers_1.getSupportedDrivers)(this.sf);
        const playing = Playback.Playing.is(this.sf.playback);
        // pause playing to avoid glitches
        if (playing) {
            this.sf.dispatch("internalPause", {}).expect(state_flow_1.ResultKind.OK);
        }
        let result = state_flow_1.Result.ignore();
        let next = current >= drivers.length - 1 ? 0 : current + 1;
        while (result.kind === state_flow_1.ResultKind.Error || result.kind === state_flow_1.ResultKind.Ignored) {
            result = await this.sf.dispatchAsync("driverSwitch", { driver: drivers[next] });
            next = next >= drivers.length - 1 ? 0 : next + 1;
        }
        // resume playing if needed
        if (playing) {
            (await this.sf.dispatchAsync("internalPlay", {})).expect(state_flow_1.ResultKind.OK);
        }
    }
    async nextPlayer() {
        return this.nextDriver();
    }
    /**
     * @deprecated Use `nextDriver` instead
     */
    selectPlayer(index) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotAllowed);
    }
    /**
     * @deprecated use `preferredLevel` setter instead
     */
    setPreferredLevel(level) {
        this.preferredLevel = level;
    }
    async replaceManifestUrl(url) {
        if ((0, state_flow_1.isTransitioning)(this.sf)) {
            throw new Error("cannot switch player while state is in transition");
        }
        // pause playing to avoid glitches
        this.sf.dispatch("internalPause", {}).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
        (await this.sf.dispatchAsync("manifestReplaceUrl", { url })).expect(state_flow_1.ResultKind.OK);
    }
    [Symbol.dispose]() {
        this.dispose();
    }
    dispose(reason) {
        (0, context_1.cancel)(this.ctx, reason !== null && reason !== void 0 ? reason : "player is disposed");
        this.isDisposed = true;
    }
}
exports.Player = Player;
Player.displayName = "Player";
//# sourceMappingURL=player.js.map